/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Youtube, Sparkles, Users, TrendingUp, Search, Heart, Mail, BookOpen, Link2, Award, UserCircle } from 'lucide-react';

const CreatorsPage: React.FC = () => {
  // Update page title and meta description for SEO, and scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'For YouTube Guitar Instructors - Guituitive Creator Program';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Join Guituitive\'s creator program for YouTube guitar instructors. Learn how RAG technology helps students discover your lessons while you maintain full credit and control of your content.'
      );
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Back Button */}
      <header className="border-b sticky top-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between max-w-6xl">
          <Link to="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to App
            </Button>
          </Link>
          <h1 className="text-xl font-bold">For Creators</h1>
          <div className="w-24"></div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <Youtube className="h-16 w-16 text-red-500" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            For YouTube Guitar Instructors
          </h2>
          <p className="text-xl text-muted-foreground mb-4 leading-relaxed max-w-3xl mx-auto">
            We're not here to compete with your teaching—we're here to{' '}
            <span className="font-semibold text-foreground">amplify it</span>.
          </p>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
            Guituitive helps students discover and learn from your lessons through intelligent search technology,
            while you retain full credit and control of your content.
          </p>
        </section>

        {/* What We're NOT */}
        <section className="mb-16">
          <Card className="border-2 border-green-500/20 bg-green-500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <Heart className="h-5 w-5" />
                We're Not Here to Replace You
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-muted-foreground">
                Guituitive is a <strong>fretboard visualization tool</strong>, not a teaching platform.
                We don't host lessons, create competing content, or try to keep students away from your channel.
              </p>
              <p className="text-muted-foreground">
                Instead, we help students <em>find</em> your lessons when they're searching for answers—then
                send them directly to your videos with full attribution.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* What is RAG? */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-6">What is RAG Technology?</h3>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-start gap-4">
                <Sparkles className="h-6 w-6 text-purple-500 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-2">Simple Explanation</h4>
                  <p className="text-sm text-muted-foreground">
                    <strong>RAG (Retrieval-Augmented Generation)</strong> is AI technology that makes your content
                    searchable and discoverable in a conversational way. Think of it as a smart librarian who knows
                    exactly which of your videos answers a student's specific question.
                  </p>
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <h4 className="font-semibold">How It Works:</h4>
                <ol className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/10 text-purple-500 flex items-center justify-center text-xs font-bold">
                      1
                    </span>
                    <span>
                      We analyze your video content (titles, descriptions, transcripts) and create a searchable index
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/10 text-purple-500 flex items-center justify-center text-xs font-bold">
                      2
                    </span>
                    <span>
                      When students ask questions in Guituitive's chat, the AI searches your indexed content
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/10 text-purple-500 flex items-center justify-center text-xs font-bold">
                      3
                    </span>
                    <span>
                      The AI provides an answer with a direct link to your video and full attribution to you
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/10 text-purple-500 flex items-center justify-center text-xs font-bold">
                      4
                    </span>
                    <span>
                      Students click through to watch your full lesson on YouTube
                    </span>
                  </li>
                </ol>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Important:</strong> Your content always stays on your channel.
                  We only create text summaries for search—we never host, embed, or copy your videos.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Benefits for Creators */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-8">How This Benefits You</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Benefit 1 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Search className="h-5 w-5 text-blue-500" />
                  More Discoverability
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Students searching for specific techniques or concepts can find your relevant lessons
                  even if they've never heard of your channel before.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 2 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  Targeted Traffic
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Students arrive at your videos with a specific question in mind, making them more likely
                  to watch, engage, and subscribe.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 3 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Award className="h-5 w-5 text-purple-500" />
                  Full Attribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Every reference to your content includes your channel name, video title, and a direct
                  link. You get proper credit for your expertise.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 4 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-5 w-5 text-orange-500" />
                  Help More Students
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Your lessons reach students who need them most, at exactly the right moment in their
                  learning journey.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 5 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <UserCircle className="h-5 w-5 text-pink-500" />
                  Featured Creator Profile
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Get highlighted in our Contributors section with your own profile page. Minimal effort
                  required—just provide content or use your existing website.
                </p>
              </CardContent>
            </Card>

            {/* Benefit 6 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Sparkles className="h-5 w-5 text-yellow-500" />
                  Zero Time Investment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Once you opt in, we handle everything. You focus on creating great content while we
                  make it more discoverable.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Example Use Case */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-6">Here's How It Works in Practice</h3>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div>
                  <div className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-2">
                    Student Question:
                  </div>
                  <p className="text-muted-foreground italic">
                    "How do I connect the minor pentatonic scale positions on the guitar?"
                  </p>
                </div>

                <Separator />

                <div>
                  <div className="text-sm font-semibold text-green-600 dark:text-green-400 mb-2">
                    Guituitive's AI Response:
                  </div>
                  <div className="bg-muted/50 p-4 rounded-lg text-sm text-muted-foreground space-y-2">
                    <p>
                      "The key to connecting pentatonic positions is understanding how they overlap.
                      Each position shares notes with the adjacent positions..."
                    </p>
                    <div className="flex items-center gap-2 pt-2 border-t border-border/50 mt-3">
                      <Link2 className="h-4 w-4 text-blue-500" />
                      <span className="text-xs">
                        <strong>Learn more:</strong>{' '}
                        <span className="text-blue-500">
                          "Connecting Pentatonic Scale Positions" by [Instructor Name]
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <div className="text-sm font-semibold text-purple-600 dark:text-purple-400 mb-2">
                    Result:
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Student gets a helpful answer, sees your name and video title, and clicks through
                    to watch your full lesson on YouTube. You get a new view, potential subscriber,
                    and help a student learn.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Separator className="my-16" />

        {/* Creator Profile Feature */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-6">Get Featured in Our Contributors Section</h3>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-start gap-4">
                <UserCircle className="h-8 w-8 text-purple-500 flex-shrink-0 mt-1" />
                <div className="space-y-3">
                  <h4 className="font-semibold text-lg">Your Own Creator Profile Page</h4>
                  <p className="text-sm text-muted-foreground">
                    When you join, you'll be highlighted in Guituitive's Contributors section where students
                    can discover instructors. Your profile showcases your expertise and links directly to your content.
                  </p>

                  <div className="bg-muted/50 p-4 rounded-lg space-y-2 mt-4">
                    <h5 className="text-sm font-semibold">Two Easy Options:</h5>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <div className="flex gap-2">
                        <span className="text-purple-500 font-bold">1.</span>
                        <span>
                          <strong>Provide us content:</strong> Give us a bio, photo, and links. We'll create
                          a beautiful profile page for you.
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-purple-500 font-bold">2.</span>
                        <span>
                          <strong>Use your existing site:</strong> Already have an about page, press kit, or
                          website? Just send us the link and we'll use that.
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mt-4">
                    Either way, it takes less than 5 minutes of your time. We handle all the technical work
                    to showcase you professionally.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <Separator className="my-16" />

        {/* Your Control */}
        <section className="mb-16">
          <h3 className="text-3xl font-bold mb-6">You're Always in Control</h3>
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <BookOpen className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-2">Opt-In Only</h4>
                    <p className="text-sm text-muted-foreground">
                      We only index content from creators who explicitly want to participate. We reach out
                      first and get your permission.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Heart className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-2">Your Content Stays Yours</h4>
                    <p className="text-sm text-muted-foreground">
                      We don't host, download, or redistribute your videos. All traffic goes directly to
                      your YouTube channel where you maintain full control and monetization.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <Award className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-2">Remove Anytime</h4>
                    <p className="text-sm text-muted-foreground">
                      Changed your mind? Just let us know and we'll remove your content from our index
                      immediately, no questions asked.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        <Separator className="my-16" />

        {/* CTA Section */}
        <section className="mb-16">
          <div className="text-center py-12 px-6 bg-gradient-to-r from-blue-600/10 to-purple-600/10 rounded-lg border border-border">
            <Youtube className="h-12 w-12 mx-auto mb-6 text-red-500" />
            <h3 className="text-2xl font-bold mb-4">Interested in Joining?</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              We're looking for passionate guitar instructors who want to help more students discover
              their content. If this sounds like you, we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href="mailto:creators@guituitive.com">
                <Button size="lg" className="font-semibold">
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Us: creators@guituitive.com
                </Button>
              </a>
              <Link to="/about">
                <Button variant="outline" size="lg">
                  Learn More About Guituitive
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Preview */}
        <section className="mb-16">
          <h3 className="text-2xl font-bold mb-6">Common Questions</h3>
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Does this cost me anything?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  No. This is completely free for creators. We're building Guituitive to help guitarists
                  learn, and that includes helping them find the best teachers.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">How much time does this require from me?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Very little. Once you say yes, we handle the indexing automatically. You just keep
                  making great content like you always do.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">What if students stop watching my full videos?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  The AI provides brief context, but always directs students to watch your full lesson
                  for the complete explanation. Our goal is to drive engaged traffic to your channel,
                  not replace your teaching.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Can I see how my content is being used?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Absolutely. We're happy to share examples of how your content appears in responses and
                  discuss any concerns you have.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t mt-20">
        <div className="container mx-auto px-4 py-8 text-center text-sm text-muted-foreground">
          <p>© 2024-2026 Entangled Harmony Labs, LLC</p>
          <p className="mt-1">All Rights Reserved</p>
        </div>
      </footer>

      {/* Structured Data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Guituitive Creator Program for YouTube Guitar Instructors",
          "description": "Join Guituitive's creator program for YouTube guitar instructors. Learn how RAG technology helps students discover your lessons while you maintain full credit and control.",
          "url": "https://guituitive.com/creators",
          "about": {
            "@type": "Thing",
            "name": "YouTube Creator Partnership Program"
          },
          "author": {
            "@type": "Organization",
            "name": "Entangled Harmony Labs, LLC"
          },
          "keywords": "YouTube guitar instructors, guitar teaching, RAG technology, content creators, guitar lessons, music education"
        })}
      </script>
    </div>
  );
};

export default CreatorsPage;
